import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
export function FAQSection() {
  const faqs = [{
    question: 'What is real-time insurance eligibility verification?',
    answer: "Real-time eligibility verification allows healthcare practices to electronically confirm a patient's insurance coverage status, benefits, and plan details in seconds by querying payer networks directly — without phone calls, faxes, or portal logins."
  }, {
    question: 'How quickly are reports returned?',
    answer: 'Most eligibility reports are returned within seconds of submitting a request. Results include coverage status, subscriber details, and benefit information in a structured, easy-to-read format.'
  }, {
    question: 'What information is included in the report?',
    answer: 'Reports include provider and subscriber details, insurance carrier and payer ID, coverage status, plan type, network status, and detailed benefit breakdowns including deductibles, copays, coinsurance, and out-of-pocket maximums.'
  }, {
    question: 'Is this for dental practices only?',
    answer: "No. enSwift is designed for multi-specialty healthcare organizations including medical, dental, behavioral health, women's health, specialty care, vision, therapy, and more. It supports eligibility verification across a wide range of provider types and payer networks."
  }, {
    question: 'How does usage-based pricing work?',
    answer: 'You pay only for the eligibility checks you run each month. There are no upfront fees, no bundled software requirements, and no long-term contracts. As your monthly volume increases, your per-request cost decreases.'
  }, {
    question: 'Is the platform HIPAA compliant?',
    answer: 'Yes. enSwift is built with HIPAA-compliant security practices to protect patient health information. All data is transmitted securely and handled in accordance with healthcare privacy standards.'
  }, {
    question: 'Do I need to install software?',
    answer: 'No. enSwift is a secure web application accessible from any browser. There is nothing to download or install — just sign up and start verifying.'
  }];
  return <section id="faq" className="py-20 lg:py-28 bg-brand-bg" data-id="element-34">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-35">
        <div className="text-center mb-12" data-id="element-36">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-37">
            Frequently Asked Questions
          </motion.h2>
        </div>
        <div className="space-y-4" data-id="element-38">
          {faqs.map((faq, i) => <FAQItem key={i} question={faq.question} answer={faq.answer} index={i} data-id="element-39" />)}
        </div>
      </div>
    </section>;
}
function FAQItem({
  question,
  answer,
  index
}: {
  question: string;
  answer: string;
  index: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: index * 0.1
  }} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-id="element-40">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-6 text-left focus:outline-none focus-visible:bg-slate-50" aria-expanded={isOpen} data-id="element-41">
        <span className="text-lg font-semibold text-slate-900 pr-8" data-id="element-42">
          {question}
        </span>
        <ChevronDownIcon className={`w-5 h-5 text-brand-teal shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-43" />
      </button>
      <AnimatePresence data-id="element-44">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-45">
            <div className="px-6 pb-6 text-slate-600 leading-relaxed border-t border-slate-100 pt-4" data-id="element-46">
              {answer}
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}